import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Briefcase,
  Phone,
  Check,
  ChevronDown,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  getDialCode,
  getPhonePlaceholder,
} from "../constants/countries";
import { useUser } from "../context/UserContext";
import logoImg from "../assets/HelixaLogo-large.svg";
import { useSpecialties } from "../hooks/queries/useSpecialties";
import { SPECIALTY_COUNTRIES, COUNTRY_LABELS, type SpecialtyCountry } from "../constants/specialtyCountries";
import { TURKIYE_ENABLED } from "../config/features";
import {
  createUserProfileSchema,
  type CreateUserProfileInput,
} from "../lib/validation/schemas/auth.schema";
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from "../config/urls";
import "./ProfileSetupModal.css";

// During signup the user has no auth context, so the in-app embed viewer
// route is the wrong target — its "Back" button lands on the login screen.
// Point legal links at the canonical helixa.com.au pages instead.
const stripEmbedParam = (url: string): string => {
  try {
    const u = new URL(url);
    u.searchParams.delete("embed");
    return u.toString();
  } catch {
    return url;
  }
};
const PRIVACY_POLICY_PUBLIC_URL = stripEmbedParam(PRIVACY_POLICY_URL);
const TERMS_OF_SERVICE_PUBLIC_URL = stripEmbedParam(TERMS_OF_SERVICE_URL);

// Only AU and TR are supported today, so a single timezone check is enough:
// Turkey has one IANA zone, everything else defaults to Australia. With the
// Türkiye gate off there is nothing to detect, and returning AU unconditionally
// also settles the default dial code below, since that keys off this result.
const detectPracticeCountry = (): SpecialtyCountry => {
  if (!TURKIYE_ENABLED) return "AU";
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone === "Europe/Istanbul" ? "TR" : "AU";
  } catch {
    return "AU";
  }
};

interface Props {
  prefillFirstName?: string;
  prefillLastName?: string;
}

export default function ProfileSetupModal({
  prefillFirstName = "",
  prefillLastName = "",
}: Props) {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { refreshUserProfile } = useUser();
  const { data: specialties = [] } = useSpecialties();

  const isTemporaryName =
    prefillFirstName.trim().toLowerCase() === "new" &&
    prefillLastName.trim().toLowerCase() === "user";

  // UI-only state (not part of schema)
  const [phoneCountryCode, setPhoneCountryCode] = useState(() =>
    detectPracticeCountry() === "TR" ? "+90" : DEFAULT_COUNTRY_CODE
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showPracticeCountryDropdown, setShowPracticeCountryDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === phoneCountryCode) || COUNTRY_CODES[0];
  const phonePlaceholder = getPhonePlaceholder(phoneCountryCode);

  const profileSchema = useMemo(() => createUserProfileSchema(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateUserProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: isTemporaryName ? "" : prefillFirstName,
      lastName: isTemporaryName ? "" : prefillLastName,
      practiceName: "",
      country: detectPracticeCountry(),
      positionInPractice: "",
      phoneNumber: "",
    },
  });

  const positionInPractice = watch("positionInPractice");
  const practiceCountry = watch("country");
  const specialtiesForCountry = practiceCountry
    ? specialties.filter((s) => s.country === practiceCountry)
    : [];

  // First schema error or server error shown in the banner
  const formError = Object.values(errors)[0]?.message;
  const displayError = serverError || formError;

  const onSubmit = handleSubmit(async (data) => {
    if (!termsAccepted) {
      setServerError(t("profileSetup.acceptTermsRequired"));
      return;
    }
    setServerError("");
    try {
      setLoading(true);
      const dialCode = getDialCode(phoneCountryCode);
      const createUserProfile = httpsCallable(functions, "createUserProfile");
      await createUserProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        practiceName: data.practiceName,
        country: data.country,
        positionInPractice: data.positionInPractice,
        specialty: data.specialty,
        phoneNumber: `${dialCode}${data.phoneNumber.trim()}`,
        // Recorded server-side as termsAcceptedAt + termsVersion (proof of consent)
        termsAccepted,
      });
      await refreshUserProfile();
      navigate("/main", { replace: true });
    } catch (err: any) {
      console.error("Profile setup save error:", err);
      if (err?.code === "functions/permission-denied") {
        setServerError("Permission denied while saving profile. Please sign in again.");
      } else if (err?.code === "functions/invalid-argument") {
        // Most likely a stale cached bundle sending an outdated payload
        // (e.g. missing termsAccepted after a backend update). Retrying the
        // same payload cannot succeed; a reload fetches the current bundle.
        setServerError("Your session is out of date. Please refresh the page and try again.");
      } else {
        setServerError(t("profileSetup.saveFailed"));
      }
    } finally {
      setLoading(false);
    }
  });

  return (
    <div
      className="psm-overlay"
      onClick={() => {
        setShowFieldDropdown(false);
        setShowCountryDropdown(false);
        setShowPracticeCountryDropdown(false);
      }}
    >
      <div className="psm-modal">
        <div className="psm-header">
          <img
            src={logoImg}
            alt="Helixa AI"
            className="psm-logo"
          />
          <h2 className="psm-title">{t("profileSetup.title")}</h2>
          <p className="psm-subtitle">{t("profileSetup.subtitle")}</p>
        </div>

        {displayError && (
          <div className="psm-error-banner">
            <AlertCircle size={14} className="psm-error-icon" />
            <span>{displayError}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="psm-form">
          <div className="psm-name-row">
            <div className="psm-field psm-field--flex">
              <label className="psm-label">
                {t("common:fields.firstName")} <span className="psm-required">*</span>
              </label>
              <div className="psm-input-wrap">
                <input
                  {...register("firstName")}
                  type="text"
                  placeholder={t("common:fields.firstNamePlaceholder")}
                  className="psm-input"
                  disabled={loading}
                  maxLength={100}
                />
              </div>
            </div>

            <div className="psm-field psm-field--flex">
              <label className="psm-label">
                {t("common:fields.lastName")} <span className="psm-required">*</span>
              </label>
              <div className="psm-input-wrap">
                <input
                  {...register("lastName")}
                  type="text"
                  placeholder={t("common:fields.lastNamePlaceholder")}
                  className="psm-input"
                  disabled={loading}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          <div className="psm-field">
            <label className="psm-label">
              {t("profileSetup.practiceName")} <span className="psm-required">*</span>
            </label>
            <div className="psm-input-wrap">
              <Briefcase size={14} className="psm-input-icon" />
              <input
                {...register("practiceName")}
                type="text"
                placeholder={t("profileSetup.practiceNamePlaceholder")}
                className="psm-input"
                disabled={loading}
                maxLength={200}
              />
            </div>
          </div>

          <div className="psm-field psm-field--relative">
            <label className="psm-label">
              {t("profileSetup.practiceCountry")} <span className="psm-required">*</span>
            </label>
            <button
              type="button"
              className="psm-input-wrap psm-select-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowPracticeCountryDropdown((v) => !v);
                setShowFieldDropdown(false);
                setShowCountryDropdown(false);
              }}
              disabled={loading}
            >
              <div className="psm-select-content">
                <span
                  className={`psm-select-value${practiceCountry ? " psm-select-value--filled" : ""}`}
                >
                  {practiceCountry ? COUNTRY_LABELS[practiceCountry] : t("profileSetup.selectPracticeCountry")}
                </span>
              </div>
              <ChevronDown
                size={13}
                className={`psm-chevron${showPracticeCountryDropdown ? " psm-chevron--open" : ""}`}
              />
            </button>
            {showPracticeCountryDropdown && (
              <div
                className="psm-dropdown"
                onClick={(e) => e.stopPropagation()}
              >
                {SPECIALTY_COUNTRIES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={`psm-option-btn${practiceCountry === code ? " psm-option-btn--active" : ""}`}
                    onClick={() => {
                      setValue("country", code, { shouldValidate: true });
                      // Country is locked forever after signup, so a stale
                      // selection from a different country must be cleared.
                      // Not validated here — this reset shouldn't surface a
                      // "select specialty" error before the user submits.
                      setValue("positionInPractice", "");
                      setValue("specialty", undefined);
                      setShowPracticeCountryDropdown(false);
                    }}
                  >
                    <span>{COUNTRY_LABELS[code]}</span>
                    {practiceCountry === code && <Check size={12} className="psm-option-check" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="psm-field psm-field--relative">
            <label className="psm-label">
              {t("profileSetup.fieldOfPractice")} <span className="psm-required">*</span>
            </label>
            <button
              type="button"
              className="psm-input-wrap psm-select-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (!practiceCountry) return;
                setShowFieldDropdown((v) => !v);
                setShowCountryDropdown(false);
                setShowPracticeCountryDropdown(false);
              }}
              disabled={loading || !practiceCountry}
            >
              <div className="psm-select-content">
                <Briefcase size={14} className="psm-input-icon" />
                <span
                  className={`psm-select-value${positionInPractice ? " psm-select-value--filled" : ""}`}
                >
                  {positionInPractice || (practiceCountry ? t("profileSetup.selectClinicianType") : t("profileSetup.selectPracticeCountryFirst"))}
                </span>
              </div>
              <ChevronDown
                size={13}
                className={`psm-chevron${showFieldDropdown ? " psm-chevron--open" : ""}`}
              />
            </button>
            {showFieldDropdown && (
              <div
                className="psm-dropdown"
                onClick={(e) => e.stopPropagation()}
              >
                {specialtiesForCountry.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`psm-option-btn${positionInPractice === s.name ? " psm-option-btn--active" : ""}`}
                    onClick={() => {
                      setValue("positionInPractice", s.name, { shouldValidate: true });
                      setValue("specialty", s.id, { shouldValidate: true });
                      setShowFieldDropdown(false);
                    }}
                  >
                    <span>{s.name}</span>
                    {positionInPractice === s.name && <Check size={12} className="psm-option-check" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="psm-field">
            <label className="psm-label">
              {t("profileSetup.phoneNumber")} <span className="psm-required">*</span>
            </label>
            <div className="psm-phone-row">
              <div className="psm-country-wrap">
                <button
                  type="button"
                  className="psm-country-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCountryDropdown((v) => !v);
                    setShowFieldDropdown(false);
                    setShowPracticeCountryDropdown(false);
                  }}
                  disabled={loading}
                >
                  <span className="psm-country-code">{getDialCode(selectedCountry.code)}</span>
                  <ChevronDown
                    size={11}
                    className={`psm-chevron${showCountryDropdown ? " psm-chevron--open" : ""}`}
                  />
                </button>
                {showCountryDropdown && (
                  <div
                    className="psm-dropdown psm-dropdown--scroll"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        className={`psm-option-btn psm-option-btn--country${phoneCountryCode === c.code ? " psm-option-btn--active" : ""}`}
                        onClick={() => {
                          setPhoneCountryCode(c.code);
                          setShowCountryDropdown(false);
                        }}
                      >
                        <span>{c.label}</span>
                        {phoneCountryCode === c.code && <Check size={11} className="psm-option-check" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="psm-input-wrap psm-input-wrap--flex">
                <Phone size={14} className="psm-input-icon" />
                <input
                  {...register("phoneNumber")}
                  type="tel"
                  placeholder={phonePlaceholder}
                  className="psm-input"
                  disabled={loading}
                  maxLength={20}
                />
              </div>
            </div>
          </div>

          <label className="psm-terms">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                setServerError("");
              }}
              className="psm-terms-checkbox"
            />
            <span>
              {t("profileSetup.termsPrefix")} {" "}
              <a href={PRIVACY_POLICY_PUBLIC_URL} target="_blank" rel="noopener noreferrer" className="psm-link">
                {t("profileSetup.privacyPolicy")}
              </a>
              {" "}{t("profileSetup.and")}{" "}
              <a href={TERMS_OF_SERVICE_PUBLIC_URL} target="_blank" rel="noopener noreferrer" className="psm-link">
                {t("profileSetup.termsOfService")}
              </a>
              .
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !termsAccepted}
            className="psm-submit-btn"
          >
            {loading ? <Loader2 size={16} className="psm-spinner" /> : <Check size={16} />}
            <span>{loading ? t("profileSetup.saving") : t("profileSetup.continue")}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
